import { useEffect, useState } from 'react';
import axios from 'axios';
import { Lightning, Target, ChartLineUp, CircleNotch, Info } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BALL_LABELS = {
  ball_1: 'Ball 1',
  ball_2: 'Ball 2',
  ball_3: 'Ball 3',
  ball_4: 'Ball 4',
  ball_5: 'Ball 5',
};

const TIER_STYLE = {
  ELITE:    { ring: 'ring-emerald-400', bg: 'bg-emerald-500',  text: 'text-emerald-50',  badge: 'bg-emerald-600 text-white' },
  STRONG:   { ring: 'ring-blue-400',    bg: 'bg-blue-500',     text: 'text-blue-50',     badge: 'bg-blue-600 text-white' },
  MODERATE: { ring: 'ring-amber-400',   bg: 'bg-amber-500',    text: 'text-amber-50',    badge: 'bg-amber-600 text-white' },
  FALLBACK: { ring: 'ring-slate-400',   bg: 'bg-slate-400',    text: 'text-slate-50',    badge: 'bg-slate-500 text-white' },
};

export default function ChampionComboBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMethod, setShowMethod] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/analysis/champion-combo-prediction`, { withCredentials: true });
        if (live) setData(res.data);
      } catch (e) {
        if (live) setError(e?.response?.data?.error || e.message || 'Failed to load');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 rounded-xl px-5 py-6 text-white" data-testid="champion-combo-banner-loading">
        <div className="flex items-center gap-3">
          <CircleNotch size={20} className="animate-spin" />
          <span className="text-sm">Computing champion-combo prediction…</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl px-4 py-3 text-sm" data-testid="champion-combo-banner-error">
        Champion-combo prediction unavailable: {error}
      </div>
    );
  }
  if (!data) return null;

  const { next_draw_number, last_draw_number, last_draw_date, recommended_ticket = [],
          per_ball_champion_picks = {}, best_rank_slot_picks = [], champion_filters = {},
          method_summary = {} } = data;

  return (
    <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 rounded-xl text-white border border-indigo-700/40 shadow-2xl overflow-hidden" data-testid="champion-combo-banner">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/10 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Lightning size={20} weight="fill" className="text-amber-400" />
            <h2 className="font-bold text-base tracking-tight">Champion-Combo Prediction</h2>
            <span className="text-[9px] font-bold bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded">EMPIRICAL</span>
          </div>
          <p className="text-[11px] text-white/70 leading-snug max-w-3xl">
            Per-ball predictions filtered through the empirically strongest window-set <span className="font-mono">∩</span>
            {' '}intersection (champion combo) discovered by 30-draw rolling backtest.
            {' '}Top-25 cross-ball aggregation catches <span className="text-amber-300 font-bold">{method_summary.top_25_hit_rate_pct}%</span> of winners;
            {' '}top-50 catches <span className="text-amber-300 font-bold">{method_summary.top_50_hit_rate_pct}%</span>.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className="text-[10px] text-white/50 uppercase tracking-wider">Next draw</div>
          <div className="font-mono font-bold text-2xl text-amber-300">#{next_draw_number}</div>
          <div className="text-[10px] text-white/60 font-mono">last #{last_draw_number} · {last_draw_date}</div>
        </div>
      </div>

      {/* Recommended Ticket */}
      <div className="px-5 py-4 bg-black/20" data-testid="cc-recommended-ticket">
        <div className="flex items-center gap-2 mb-3">
          <Target size={14} weight="bold" className="text-emerald-300" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-300">Recommended Ticket</span>
          <span className="text-[10px] text-white/50">— one champion pick per ball</span>
        </div>
        <div className="grid grid-cols-5 gap-3">
          {recommended_ticket.map(r => {
            const style = TIER_STYLE[r.tier] || TIER_STYLE.MODERATE;
            return (
              <div key={r.ball} className="bg-white/5 border border-white/10 rounded-lg p-3 text-center" data-testid={`cc-rec-${r.ball}`}>
                <div className="text-[9px] uppercase tracking-wider text-white/50 mb-1.5">{BALL_LABELS[r.ball]}</div>
                <div className={`inline-flex w-14 h-14 rounded-full items-center justify-center font-mono font-bold text-2xl text-white ring-4 ring-offset-2 ring-offset-slate-900 ${style.ring} ${style.bg}`}>
                  {r.number}
                </div>
                <div className={`mt-2 inline-block text-[9px] font-bold px-2 py-0.5 rounded uppercase ${style.badge}`}>
                  {r.tier}
                </div>
                <div className="mt-1.5 text-[9px] text-white/60 font-mono">
                  pos-votes <span className="text-white">{r.ball_votes}</span> · cross <span className="text-white">{r.cross_total}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-Ball Champion Filter Detail */}
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <ChartLineUp size={14} weight="bold" className="text-blue-300" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-blue-300">Per-Ball Champion Filters</span>
          <span className="text-[10px] text-white/50">— numbers passing the empirically strongest window intersection</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="px-2 py-2 text-[10px] uppercase text-white/50 font-bold w-16">Ball</th>
                <th className="px-2 py-2 text-[10px] uppercase text-white/50 font-bold w-44">Filter</th>
                <th className="px-2 py-2 text-[10px] uppercase text-white/50 font-bold w-24">Hit-Rate</th>
                <th className="px-2 py-2 text-[10px] uppercase text-white/50 font-bold">Surviving Numbers (sorted by ball-position vote concentration)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(per_ball_champion_picks).map(([bk, info]) => {
                const cf = champion_filters[bk] || {};
                const tier = TIER_STYLE[cf.tier] || TIER_STYLE.MODERATE;
                return (
                  <tr key={bk} className="border-b border-white/5" data-testid={`cc-row-${bk}`}>
                    <td className="px-2 py-2 font-bold">{BALL_LABELS[bk]}</td>
                    <td className="px-2 py-2 font-mono text-white/80">{cf.rule}</td>
                    <td className="px-2 py-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tier.badge}`}>{cf.hit_rate_pct?.toFixed(1)}%</span>
                      <span className="text-[10px] text-white/50 ml-1.5">{cf.lift?.toFixed(1)}× rand</span>
                    </td>
                    <td className="px-2 py-2">
                      {info.candidates && info.candidates.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {info.candidates.map((c, idx) => (
                            <span key={c.number}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-xs ${idx === 0 ? `${tier.bg} text-white font-bold ring-1 ${tier.ring}` : 'bg-white/10 text-white/90'}`}
                                  title={`matched ${c.matched_windows.map(w => `W${w}`).join('+')} · ball-votes ${c.ball_votes} · cross ${c.cross_total}`}
                                  data-testid={`cc-cand-${bk}-${c.number}`}>
                              {c.full_match && <span className="text-[9px]">⭐</span>}
                              <span>{c.number}</span>
                              <span className="text-[9px] opacity-70">({c.ball_votes}/{c.cross_total})</span>
                            </span>
                          ))}
                          <span className="text-[10px] text-white/40 ml-1">{info.n_candidates} total</span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-white/40 italic">no number satisfied this filter for the next draw</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[10px] text-white/40 italic">
          ⭐ = matched ALL windows in the filter. Format <span className="font-mono">N (ball-votes/cross-total)</span>. Hover for matched windows.
        </p>
      </div>

      {/* Best (Window, Rank) Slots */}
      {best_rank_slot_picks.length > 0 && (
        <div className="px-5 py-4 border-t border-white/10 bg-black/20" data-testid="cc-rank-slots">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-fuchsia-300">High-Reliability (Window, Rank) Slot Picks</span>
            <span className="text-[10px] text-white/50">— picks from the (window, rank) slots that historically hit ≥13% of the time</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {best_rank_slot_picks.map((s, i) => (
              <div key={`${s.window}-${s.rank}-${s.ball}-${i}`} className="bg-white/5 border border-fuchsia-400/20 rounded-lg px-3 py-2 flex items-center gap-2" data-testid={`cc-slot-${i}`}>
                <div className="inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-base bg-fuchsia-500/20 text-fuchsia-200 ring-1 ring-fuchsia-300/30">
                  {s.predicted_number}
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] font-mono text-white/80">W{s.window} #{s.rank} → {BALL_LABELS[s.ball]}</span>
                  <span className="text-[9px] text-fuchsia-300">{s.hit_rate_pct.toFixed(1)}% hit · {s.lift.toFixed(1)}× rand</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Method footer */}
      <div className="px-5 py-3 border-t border-white/10 bg-black/30">
        <button onClick={() => setShowMethod(s => !s)} className="text-[11px] text-white/60 hover:text-white/90 flex items-center gap-1.5" data-testid="cc-toggle-method">
          <Info size={12} /> {showMethod ? 'Hide' : 'About'} this method
        </button>
        {showMethod && (
          <div className="mt-2 text-[11px] text-white/70 leading-relaxed space-y-1.5">
            <p>
              Findings derived from a 30-draw rolling backtest (#46087 → #46116) using the same prediction
              algorithms as W11/W19/W21 and the per-window-ball-predictions endpoint:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5 text-white/60">
              <li><span className="text-white">Cross-ball top-25</span> aggregation catches <span className="text-amber-300 font-bold">44%</span> of winners (avg 2.2/5 per draw); top-50 catches <span className="text-amber-300 font-bold">97.3%</span>.</li>
              <li><span className="text-white">Champion combos</span>: each ball has an empirically optimal window-set whose intersection of top-5 picks predicts hits with up to <span className="text-emerald-300 font-bold">12.5×</span> random.</li>
              <li><span className="text-white">Best signal</span>: <span className="font-mono">W18 ∩ W19 ∩ W22</span> for Ball 3 hit <span className="text-emerald-300 font-bold">25%</span> of the time (12/3 in 30 draws).</li>
              <li><span className="text-white">High-reliability slots</span>: specific (window, rank) pairings (e.g. W21 #5 → B4/B5) hit 16.7% — 8.4× random.</li>
              <li>Honest limit: even with these filters, no 5/5 outcome occurred in any backtested draw. Realistic expectation is <span className="text-amber-300 font-bold">2–3 hits / 5</span>.</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
